import { useEffect, useState } from "react";
import { authedFetch } from "@/lib/auth";

/**
 * A walkthrough step's screenshot as an object URL (auth header needed,
 * so no plain <img src>). Fetches only while `enabled`; the URL is
 * revoked when the step changes or the component unmounts.
 */
interface CacheEntry {
  url: string;
  refCount: number;
  lastUsed: number;
}

const screenshotBlobCache = new Map<string, CacheEntry>();
const inFlightRequests = new Map<string, Promise<string | null>>();
const MAX_CACHE_SIZE = 50;

function evictLruIfNecessary() {
  if (screenshotBlobCache.size <= MAX_CACHE_SIZE) return;
  let oldestKey: string | null = null;
  let oldestTime = Number.POSITIVE_INFINITY;

  for (const [key, entry] of screenshotBlobCache.entries()) {
    if (entry.refCount <= 0 && entry.lastUsed < oldestTime) {
      oldestTime = entry.lastUsed;
      oldestKey = key;
    }
  }

  if (oldestKey) {
    const entry = screenshotBlobCache.get(oldestKey);
    if (entry) {
      URL.revokeObjectURL(entry.url);
      screenshotBlobCache.delete(oldestKey);
    }
  }
}

/**
 * A walkthrough step's screenshot as an object URL with LRU caching and
 * in-flight request deduplication.
 */
export function useStepScreenshot(
  walkthroughId: string | null | undefined,
  stepNumber: number | null | undefined,
  enabled = true,
): { url: string | null; loading: boolean } {
  const key =
    walkthroughId && typeof stepNumber === "number" && stepNumber > 0
      ? `${walkthroughId}:${stepNumber}`
      : null;

  const [url, setUrl] = useState<string | null>(() =>
    key ? (screenshotBlobCache.get(key)?.url ?? null) : null,
  );
  const [loading, setLoading] = useState<boolean>(() =>
    Boolean(enabled && key && !screenshotBlobCache.has(key)),
  );

  useEffect(() => {
    if (!enabled || !key || !walkthroughId || typeof stepNumber !== "number") {
      setLoading(false);
      setUrl(null);
      return;
    }

    let isMounted = true;
    const existing = screenshotBlobCache.get(key);
    if (existing) {
      existing.refCount++;
      existing.lastUsed = Date.now();
      setUrl(existing.url);
      setLoading(false);
      return () => {
        existing.refCount = Math.max(0, existing.refCount - 1);
        existing.lastUsed = Date.now();
      };
    }

    setLoading(true);

    const fetchPromise =
      inFlightRequests.get(key) ||
      (async () => {
        try {
          const res = await authedFetch(
            `/api/walkthroughs/${walkthroughId}/screenshots/${stepNumber}`,
          );
          if (!res.ok) return null;
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        } catch {
          return null;
        } finally {
          inFlightRequests.delete(key);
        }
      })();

    inFlightRequests.set(key, fetchPromise);

    fetchPromise.then((blobUrl) => {
      if (!isMounted) return;
      if (blobUrl) {
        evictLruIfNecessary();
        screenshotBlobCache.set(key, {
          url: blobUrl,
          refCount: 1,
          lastUsed: Date.now(),
        });
        setUrl(blobUrl);
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
      const cached = screenshotBlobCache.get(key);
      if (cached) {
        cached.refCount = Math.max(0, cached.refCount - 1);
        cached.lastUsed = Date.now();
      }
    };
  }, [key, walkthroughId, stepNumber, enabled]);

  return { url, loading };
}
